// Pure rules over the `my-companies` answer and the two company errors.
// The server decides; these only read what it said.

import { isValidCompanyId } from './companySession';

export const COMPANY_REQUIRED = 'COMPANY_REQUIRED';
export const NOT_IN_COMPANY = 'NOT_IN_COMPANY';
export const PICKER_PATH = '/select-company';
/** The Companies & routing page: companies, invitations, Add a company, routing. */
export const COMPANIES_PATH = '/companies';

const OWNER = 'Owner';

/** `{companies, pendingInvites, multiCompany}` out of a my-companies response, or null. */
export function readMyCompanies(response) {
  if (response?.status !== 200) return null;
  const data = response?.data?.payload?.data ?? response?.data?.payload;
  if (!data || typeof data !== 'object') return null;
  return {
    companies: Array.isArray(data.companies)
      ? data.companies.filter((c) => isValidCompanyId(String(c?.companyId ?? '')))
      : [],
    pendingInvites: Array.isArray(data.pendingInvites) ? data.pendingInvites : [],
    multiCompany: data.multiCompany === true
  };
}

/**
 * What sign-in does next. Anything other than a clear "this person has a choice
 * to make" is today's flow — including an older backend that has no
 * my-companies endpoint at all.
 */
export function afterSignIn(myCompanies) {
  if (!myCompanies || !myCompanies.multiCompany) return { mode: 'today' };
  const { companies, pendingInvites } = myCompanies;
  if (companies.length <= 1 && pendingInvites.length === 0) {
    return { mode: 'today', companyId: companies[0]?.companyId ?? null };
  }
  return { mode: 'picker' };
}

/** Whether to offer Switch company: only to someone with more than one company. */
export const hasSeveralCompanies = (myCompanies) => (myCompanies?.companies?.length ?? 0) > 1;

export function currentCompany(myCompanies, companyId) {
  const companies = myCompanies?.companies ?? [];
  if (companyId) {
    const match = companies.find((c) => String(c.companyId) === String(companyId));
    if (match) return match;
  }
  return companies.length === 1 ? companies[0] : null;
}

/** Add a company: the Owner of a switched-on family, in the company they are in. */
export function canAddCompany(myCompanies, companyId) {
  if (!myCompanies?.multiCompany) return false;
  return currentCompany(myCompanies, companyId)?.role === OWNER;
}

/** Settings → Companies: shown to people with several companies or a pending invite, and to an Owner who can add one. */
export function showCompaniesSettings(myCompanies, companyId) {
  if (!myCompanies) return false;
  return (
    hasSeveralCompanies(myCompanies) ||
    myCompanies.pendingInvites.length > 0 ||
    canAddCompany(myCompanies, companyId)
  );
}

/** COMPANY_REQUIRED / NOT_IN_COMPANY out of an axios error, or null. */
export function companyErrorCode(error) {
  const response = error?.response;
  const status = response?.status;
  if (status !== 400 && status !== 403) return null;
  const body = response?.data;
  const code = body?.payload?.data?.code ?? body?.payload?.code ?? body?.code;
  if (status === 400 && code === COMPANY_REQUIRED) return COMPANY_REQUIRED;
  if (status === 403 && code === NOT_IN_COMPANY) return NOT_IN_COMPANY;
  return null;
}

/** A `?company=<id>` link: the id, when it names one of this person's companies. */
export function companyFromLink(search, myCompanies) {
  const id = new URLSearchParams(search || '').get('company');
  if (!isValidCompanyId(id)) return null;
  return (myCompanies?.companies ?? []).some((c) => String(c.companyId) === id) ? id : null;
}

/** The same search string with `company` removed, so a link switches once. */
export function withoutCompanyParam(search) {
  const params = new URLSearchParams(search || '');
  params.delete('company');
  const rest = params.toString();
  return rest ? `?${rest}` : '';
}
